import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Inquiry, sampleInquiries } from '@/mocks/inquiries';
import InquiriesList from './components/InquiriesList';
import NotificationSettings, { NOTIFICATION_STORAGE_KEY } from './components/NotificationSettings';

const ADMIN_PASSWORD = 'Greenly2024';

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [activeTab, setActiveTab] = useState<'inquiries' | 'stats' | 'settings'>('inquiries');
  const [notificationEmail, setNotificationEmail] = useState('');

  useEffect(() => {
    const session = sessionStorage.getItem('greenly_admin');
    if (session === 'true') setIsLoggedIn(true);
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      const stored = localStorage.getItem('greenly_inquiries');
      const localInquiries: Inquiry[] = stored ? JSON.parse(stored) : [];
      const combined = localInquiries.length > 0 ? localInquiries : sampleInquiries;
      setInquiries(combined);

      const notifStored = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
      if (notifStored) {
        try {
          const parsed = JSON.parse(notifStored);
          setNotificationEmail(parsed.notificationEmail || '');
        } catch {
          // ignore
        }
      }
    }
  }, [isLoggedIn]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem('greenly_admin', 'true');
      setIsLoggedIn(true);
      setLoginError('');
    } else {
      setLoginError('Incorrect password. Please try again.');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('greenly_admin');
    setIsLoggedIn(false);
    setPassword('');
  };

  const handleUpdateInquiry = (id: string, updates: Partial<Inquiry>) => {
    const updated = inquiries.map((inq) => (inq.id === id ? { ...inq, ...updates } : inq));
    setInquiries(updated);
    localStorage.setItem('greenly_inquiries', JSON.stringify(updated));
  };

  const stats = {
    total: inquiries.length,
    new: inquiries.filter((i) => i.status === 'new').length,
    read: inquiries.filter((i) => i.status === 'read').length,
    replied: inquiries.filter((i) => i.status === 'replied').length,
  };

  if (!isLoggedIn) {
    return (
      <>
        <title>Admin | Greenly Electronics Recycling</title>
        <meta name="robots" content="noindex, nofollow" />
        <div className="min-h-screen bg-[#F4FAF7] flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md">
            <div className="text-center mb-8">
              <img
                src="/images/greenly.png"
                alt="Greenly Logo"
                className="h-12 mx-auto mb-4 object-contain"
                loading="eager"
              />
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-400 text-sm mt-1">Sign in to manage inquiries</p>
            </div>

            <form onSubmit={handleLogin}>
              {loginError && (
                <div className="mb-4 bg-red-50 text-red-600 text-sm px-4 py-3 rounded-xl border border-red-200 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-error-warning-line"></i>
                  </div>
                  {loginError}
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input
                  type="email"
                  placeholder="Enter admin email"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#1B6B3A] focus:ring-2 focus:ring-[#1B6B3A]/10 transition-colors"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter admin password"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#1B6B3A] focus:ring-2 focus:ring-[#1B6B3A]/10 transition-colors"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-[#1B6B3A] hover:bg-[#2D8653] text-white font-semibold py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                Sign In
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-[#1B6B3A] hover:underline cursor-pointer">
                &larr; Back to Website
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <title>Admin | Greenly Electronics Recycling</title>
      <meta name="robots" content="noindex, nofollow" />
      <div className="min-h-screen bg-[#F4FAF7]">
        {/* Top bar */}
        <header className="bg-[#0F3D22] px-4 md:px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img
                src="/images/greenly.png"
                alt="Greenly"
                className="h-8 object-contain brightness-200"
                loading="eager"
              />
              <span className="text-white/60 text-sm hidden sm:block">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="text-green-200 text-sm hover:text-white cursor-pointer whitespace-nowrap transition-colors hidden sm:flex items-center gap-1"
              >
                <i className="ri-external-link-line"></i> View Site
              </Link>
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-4 py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-logout-box-line"></i> Logout
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          {/* Stats cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Total Inquiries', value: stats.total, icon: 'ri-inbox-line', color: 'text-gray-800', bg: 'bg-white' },
              { label: 'New', value: stats.new, icon: 'ri-mail-unread-line', color: 'text-red-500', bg: 'bg-red-50' },
              { label: 'Read / Pending', value: stats.read, icon: 'ri-eye-line', color: 'text-yellow-500', bg: 'bg-yellow-50' },
              { label: 'Replied', value: stats.replied, icon: 'ri-reply-line', color: 'text-[#1B6B3A]', bg: 'bg-[#E8F5EE]' },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-xl p-5 flex items-center gap-4">
                <div className={`w-12 h-12 flex items-center justify-center ${s.bg} rounded-xl shrink-0`}>
                  <i className={`${s.icon} ${s.color} text-xl`}></i>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{s.value}</div>
                  <div className="text-gray-400 text-xs">{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-5 flex-wrap">
            <button
              onClick={() => setActiveTab('inquiries')}
              className={`px-5 py-2.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                activeTab === 'inquiries' ? 'bg-[#1B6B3A] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#1B6B3A]'
              }`}
            >
              <i className="ri-inbox-line mr-1.5"></i>Inquiries
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`px-5 py-2.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                activeTab === 'stats' ? 'bg-[#1B6B3A] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#1B6B3A]'
              }`}
            >
              <i className="ri-bar-chart-line mr-1.5"></i>Overview
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-5 py-2.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                activeTab === 'settings' ? 'bg-[#1B6B3A] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#1B6B3A]'
              }`}
            >
              <i className="ri-settings-3-line"></i>Settings
              {!notificationEmail && (
                <span className="w-2 h-2 bg-red-400 rounded-full"></span>
              )}
            </button>
          </div>

          {activeTab === 'inquiries' ? (
            <InquiriesList inquiries={inquiries} onUpdateInquiry={handleUpdateInquiry} notificationEmail={notificationEmail} />
          ) : activeTab === 'settings' ? (
            <NotificationSettings />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Services breakdown */}
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-bold text-gray-900 mb-5">Inquiries by Service</h3>
                {(() => {
                  const serviceCounts: Record<string, number> = {};
                  inquiries.forEach((i) => {
                    const s = i.service || 'General';
                    serviceCounts[s] = (serviceCounts[s] || 0) + 1;
                  });
                  const sorted = Object.entries(serviceCounts).sort((a, b) => b[1] - a[1]);
                  const max = sorted[0]?.[1] || 1;
                  return (
                    <div className="space-y-3">
                      {sorted.map(([service, count]) => (
                        <div key={service}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-700 truncate mr-2">{service}</span>
                            <span className="text-gray-400 shrink-0">{count}</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#1B6B3A] rounded-full transition-all duration-500"
                              style={{ width: `${(count / max) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Recent activity */}
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-bold text-gray-900 mb-5">Recent Activity</h3>
                <div className="space-y-3">
                  {inquiries.slice(0, 6).map((inq) => (
                    <div key={inq.id} className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-[#E8F5EE] rounded-full shrink-0">
                        <i className="ri-user-line text-[#1B6B3A] text-xs"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{inq.name}</div>
                        <div className="text-xs text-gray-400 truncate">{inq.service}</div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${
                        inq.status === 'new' ? 'bg-red-50 text-red-500' :
                        inq.status === 'read' ? 'bg-yellow-50 text-yellow-600' : 'bg-[#E8F5EE] text-[#1B6B3A]'
                      }`}>
                        {inq.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default AdminPage;